import { useState } from 'react';
import { Flame, Check, Lock } from 'lucide-react';
import { startInnerShiftChallenge } from '@/lib/api';
import { startUpgrade } from '@/lib/billing';
import { toast } from 'sonner';

const OPTIONS = [
  { length: 7, label: '7-day streak', tagline: 'Start strong', tier: 'free' },
  { length: 14, label: '14-day streak', tagline: 'Commit to learning', tier: 'premium' },
  { length: 21, label: '21-day streak', tagline: 'Push the limits', tier: 'premium' },
  { length: 30, label: '30-day streak', tagline: 'Anchor it for life', tier: 'premium' },
];

/**
 * Streak-challenge picker — shown on the Dashboard when the user has not yet
 * chosen a challenge length (challenge_chosen === false).
 *
 * Props:
 *   tier      "full" | "lead"   — lead users can only start the free 7-day plan
 *   onStart   callback after successful start
 *   onDismiss callback for "No, thanks"
 *   compact   tighter padding for embedded views (e.g. /confidence/library)
 */
export default function ChallengePicker({
  tier = 'full',
  onStart,
  onDismiss,
  compact = false,
}) {
  const isLead = tier === 'lead';
  const defaultPick = isLead ? 7 : 14;
  const [selected, setSelected] = useState(defaultPick);
  const [submitting, setSubmitting] = useState(false);

  const handleStart = async () => {
    setSubmitting(true);
    try {
      await startInnerShiftChallenge(selected);
      toast.success(`Your ${selected}-day streak begins now`);
      onStart?.(selected);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start challenge — try again';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const wrapperPad = compact ? 'p-5' : 'p-6 md:p-8';
  const headingClass = compact
    ? 'text-2xl md:text-3xl'
    : 'text-3xl md:text-4xl';

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-amber-500/20 bg-gradient-to-br from-amber-900/25 via-zinc-900 to-zinc-950 ${wrapperPad} ${
        compact ? 'mb-6' : 'col-span-1 md:col-span-2 lg:col-span-4'
      }`}
      data-testid="challenge-picker"
    >
      <div className="absolute top-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Flame className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80">Inner Shift</span>
        </div>

        <h2 className={`${headingClass} font-serif text-white leading-tight mb-2`}>
          Choose your<br className="md:hidden" /> streak challenge
        </h2>
        <p className="text-sm text-zinc-400 mb-6">
          {isLead
            ? 'Start with our free 7-day streak. Upgrade anytime to unlock longer challenges.'
            : 'Pick a challenge and keep your streak alive for longer.'}
        </p>

        <div className="grid gap-3 mb-6 max-w-2xl">
          {OPTIONS.map((opt) => {
            const isSelected = selected === opt.length;
            const isLocked = isLead && opt.tier === 'premium';

            const handleClick = () => {
              if (isLocked) {
                toast.error('Premium plan — starting your $1 trial…');
                startUpgrade('trial');
                return;
              }
              setSelected(opt.length);
            };

            return (
              <button
                key={opt.length}
                type="button"
                onClick={handleClick}
                className={`group relative flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                  isSelected
                    ? 'bg-amber-500/10 border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.15)]'
                    : isLocked
                    ? 'bg-zinc-900/30 border-white/5 opacity-60 cursor-not-allowed'
                    : 'bg-zinc-900/40 border-white/5 hover:border-white/15'
                }`}
                data-testid={`challenge-option-${opt.length}`}
                aria-pressed={isSelected}
                aria-disabled={isLocked}
              >
                {/* Radio / Lock */}
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    isSelected ? 'border-amber-400' : 'border-zinc-600'
                  }`}
                >
                  {isLocked ? (
                    <Lock className="w-3 h-3 text-zinc-500" />
                  ) : isSelected ? (
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  ) : null}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-base font-medium ${isSelected ? 'text-white' : 'text-zinc-200'}`}>
                      {opt.label}
                    </p>
                    <span
                      className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full border ${
                        opt.tier === 'free'
                          ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'
                          : 'text-amber-300 border-amber-500/30 bg-amber-500/10'
                      }`}
                    >
                      {opt.tier}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{opt.tagline}</p>
                </div>

                {/* Hex badge */}
                <div
                  className={`relative w-14 h-16 flex items-center justify-center transition-all ${
                    isSelected ? 'opacity-100 scale-105' : isLocked ? 'opacity-30' : 'opacity-50'
                  }`}
                  aria-hidden
                >
                  <svg viewBox="0 0 64 72" className="absolute inset-0 w-full h-full">
                    <polygon
                      points="32,2 60,18 60,54 32,70 4,54 4,18"
                      fill={isSelected ? 'rgba(251,191,36,0.95)' : 'transparent'}
                      stroke={isSelected ? '#fbbf24' : '#52525b'}
                      strokeWidth="2"
                    />
                  </svg>
                  <div className="relative flex items-center gap-1 z-10">
                    <Flame className={`w-3.5 h-3.5 ${isSelected ? 'text-zinc-950' : 'text-zinc-500'}`} />
                    <span className={`text-sm font-bold ${isSelected ? 'text-zinc-950' : 'text-zinc-500'}`}>
                      {opt.length}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl items-stretch sm:items-center">
          <button
            onClick={handleStart}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-zinc-950 hover:bg-zinc-100 font-semibold px-6 py-4 rounded-full transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="challenge-start-btn"
          >
            {submitting ? 'Starting…' : (
              <>
                <Check className="w-4 h-4" strokeWidth={3} />
                Start challenge
              </>
            )}
          </button>
          {isLead && (
            <button
              type="button"
              onClick={() => startUpgrade('trial')}
              className="text-sm text-amber-300 hover:text-amber-200 underline underline-offset-2 text-center sm:text-left"
              data-testid="challenge-upgrade-link"
            >
              Unlock 14 & 21 — $1 trial
            </button>
          )}
          {onDismiss && !isLead && (
            <button
              onClick={onDismiss}
              className="text-sm text-zinc-500 hover:text-zinc-300 px-4 py-3 transition-colors"
              data-testid="challenge-dismiss-btn"
            >
              No, thanks
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
